import {
  htmlToText,
  type AttachmentUpload,
  type BrandChangeAcknowledgement,
  type Brand,
  type EmailAddress,
  type Mailbox,
  type Message,
  type OutgoingMessage,
  type ThreadDetail,
} from '@gandhi/mail-protocol';

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward';

/**
 * A draft always belongs to a mailbox, and the mailbox always belongs to a brand.
 * There is no field here for a sender, so the compose UI has nothing to render a
 * From picker from.
 */
export interface ComposeDraft {
  readonly mode: ComposeMode;
  readonly mailboxId: string;
  readonly brandId: Brand;
  readonly to: string;
  readonly cc: string;
  readonly bcc: string;
  readonly subject: string;
  readonly bodyHtml: string;
  readonly attachments: readonly AttachmentUpload[];
  readonly inReplyToThreadId?: string;
  /** Brand the conversation arrived on. Differs from `brandId` only after a rebrand. */
  readonly threadBrandId?: Brand;
  /** Set once the draft has been autosaved, so the send can retire the stored copy. */
  readonly draftId?: string;
}

/** Splits on separators that are not inside a quoted display name or angle brackets. */
function splitAddressList(value: string): string[] {
  const parts: string[] = [];
  let current = '';
  let quoted = false;
  let angled = false;
  for (const character of value) {
    if (character === '"') quoted = !quoted;
    else if (character === '<') angled = true;
    else if (character === '>') angled = false;
    else if ((character === ',' || character === ';') && !quoted && !angled) {
      parts.push(current);
      current = '';
      continue;
    }
    current += character;
  }
  parts.push(current);
  return parts;
}

export function parseAddressList(value: string): EmailAddress[] {
  return splitAddressList(value)
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((part) => {
      const angled = /^(.*)<([^>]+)>$/.exec(part);
      if (angled === null) return { address: part };
      const name = (angled[1] ?? '').trim().replace(/^"|"$/g, '');
      const address = (angled[2] ?? '').trim();
      return name === '' ? { address } : { address, name };
    });
}

export function formatAddressList(addresses: readonly EmailAddress[]): string {
  return addresses
    .map((a) => {
      if (a.name === undefined || a.name === '') return a.address;
      const name = /[,;<>"]/.test(a.name) ? `"${a.name.replace(/"/g, '')}"` : a.name;
      return `${name} <${a.address}>`;
    })
    .join(', ');
}

export interface RecipientSplit {
  /** Addresses already terminated by a separator, and so shown as chips. */
  readonly committed: readonly EmailAddress[];
  /** The trailing fragment still being typed. */
  readonly pending: string;
}

export function splitRecipients(value: string): RecipientSplit {
  const parts = splitAddressList(value);
  return {
    committed: parseAddressList(parts.slice(0, -1).join(',')),
    pending: (parts[parts.length - 1] ?? '').trimStart(),
  };
}

export function joinRecipients(committed: readonly EmailAddress[], pending: string): string {
  const head = formatAddressList(committed);
  if (head === '') return pending;
  return `${head}, ${pending}`;
}

export function subjectFor(mode: ComposeMode, subject: string): string {
  const bare = subject.replace(/^((re|fwd?)\s*:\s*)+/i, '').trim();
  if (mode === 'forward') return `Fwd: ${bare}`;
  if (mode === 'reply' || mode === 'replyAll') return `Re: ${bare}`;
  return subject;
}

export function quote(message: Message): string {
  const who = message.from.name ?? message.from.address;
  const when = message.sentAt.slice(0, 16).replace('T', ' ');
  const lines = htmlToText(message.bodyHtml)
    .split('\n')
    .map((line) => `&gt; ${escapeHtml(line)}`)
    .join('<br />');
  return `<p></p><blockquote><p>On ${when}, ${escapeHtml(who)} wrote:</p><p>${lines}</p></blockquote>`;
}

export function newDraft(mailbox: Mailbox): ComposeDraft {
  return {
    mode: 'new',
    mailboxId: mailbox.id,
    brandId: mailbox.brandId,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    bodyHtml: '',
    attachments: [],
  };
}

export interface ReplyInput {
  readonly mailbox: Mailbox;
  readonly thread: ThreadDetail;
  readonly message: Message;
  readonly all?: boolean;
}

export function replyDraft(input: ReplyInput): ComposeDraft {
  const { mailbox, thread, message, all = false } = input;
  const own = mailbox.address.toLowerCase();
  const others = [...message.to, ...message.cc].filter((a) => a.address.toLowerCase() !== own);
  const mode: ComposeMode = all ? 'replyAll' : 'reply';
  return {
    mode,
    mailboxId: mailbox.id,
    brandId: mailbox.brandId,
    to: formatAddressList([message.from]),
    cc: all ? formatAddressList(dedupe(others)) : '',
    bcc: '',
    subject: subjectFor(mode, message.subject),
    bodyHtml: quote(message),
    attachments: [],
    inReplyToThreadId: thread.id,
    threadBrandId: thread.brandId,
  };
}

export function forwardDraft(input: Omit<ReplyInput, 'all'>): ComposeDraft {
  const { mailbox, thread, message } = input;
  return {
    mode: 'forward',
    mailboxId: mailbox.id,
    brandId: mailbox.brandId,
    to: '',
    cc: '',
    bcc: '',
    subject: subjectFor('forward', message.subject),
    bodyHtml: quote(message),
    attachments: [...message.attachments].map((a) => ({
      name: a.name,
      mimeType: a.mimeType,
      content: '',
    })),
    inReplyToThreadId: thread.id,
    threadBrandId: thread.brandId,
  };
}

/**
 * Moves a draft into another mailbox. Keeps `threadBrandId` so the confirmation the
 * server demands can still name the brand the conversation started on.
 */
export function rebrandDraft(draft: ComposeDraft, mailbox: Mailbox): ComposeDraft {
  const { draftId: _retired, ...rest } = draft;
  return { ...rest, mailboxId: mailbox.id, brandId: mailbox.brandId };
}

/** True when the draft would move an existing conversation to another brand. */
export function isBrandChange(draft: ComposeDraft): boolean {
  return draft.threadBrandId !== undefined && draft.threadBrandId !== draft.brandId;
}

export function toOutgoing(
  draft: ComposeDraft,
  acknowledgement?: BrandChangeAcknowledgement,
): OutgoingMessage {
  const cc = parseAddressList(draft.cc);
  const bcc = parseAddressList(draft.bcc);
  const attachments = draft.attachments.filter((a) => a.content !== '');
  return {
    to: parseAddressList(draft.to),
    ...(cc.length > 0 ? { cc } : {}),
    ...(bcc.length > 0 ? { bcc } : {}),
    subject: draft.subject,
    bodyHtml: draft.bodyHtml,
    ...(attachments.length > 0 ? { attachments } : {}),
    ...(draft.inReplyToThreadId === undefined
      ? {}
      : { inReplyToThreadId: draft.inReplyToThreadId }),
    ...(acknowledgement === undefined ? {} : { acknowledgedBrandChange: acknowledgement }),
  };
}

export function draftRecipients(draft: ComposeDraft): string[] {
  return [
    ...parseAddressList(draft.to),
    ...parseAddressList(draft.cc),
    ...parseAddressList(draft.bcc),
  ].map((a) => a.address);
}

function dedupe(addresses: readonly EmailAddress[]): EmailAddress[] {
  const seen = new Map<string, EmailAddress>();
  for (const address of addresses) seen.set(address.address.toLowerCase(), address);
  return [...seen.values()];
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
